/**
 * Enum ids and display constants for custom playlist schedules.
 *
 * The ids mirror the backend's seeded lookup tables (CustomPlaylistScheduleType,
 * CustomPlaylistInsertType, SchedulingMode). models.json carries no constants for
 * them, so they are declared here once.
 */

/** custom_playlist_schedule_types ids. Silence is MESSAGING with `silence = 1`. */
export const SCHEDULE_TYPE = {
    DATETIME: 1,
    RECURRING: 2,
    STATION_CHANGE: 3,
    INSERT: 4,
    MESSAGING: 5,
} as const;

/**
 * custom_playlist_insert_types ids. Every schedule is saved as Replace, so it is the only one
 * the interface needs. Insert (1) is retired.
 */
export const INSERT_TYPE = {
    REPLACE: 2,
} as const;

/** scheduling_modes ids on a location zone. */
export const SCHEDULING_MODE = {
    LEGACY: 1,
    PRIORITY: 2,
} as const;

export interface DayOfWeek {
    /** The value stored in `delimited_day_ids`. */
    id: number;
    /** One letter, for a day chip. Ambiguous on its own (S, T). */
    short: string;
    abbr: string;
    name: string;
}

/**
 * The seven days in the order the players count them: Sunday = 1 … Saturday = 7.
 *
 * This is NOT ISO (Monday = 1). playercontrollers/getlists.php:322 passes
 * `delimited_day_ids` through untouched, and every player subtracts one and
 * compares with a Sunday = 0 weekday (physicalplayer common.php:6830-6836,
 * gvplayer tm_wday, virtualplayer DayOfWeek). tabasco's DaySeeder seeds
 * 1 Sunday … 7 Saturday. tabasco-react's adapter uses Monday = 1, which is a bug.
 */
export const DAY_IDS: readonly DayOfWeek[] = [
    { id: 1, short: 'S', abbr: 'Sun', name: 'Sunday' },
    { id: 2, short: 'M', abbr: 'Mon', name: 'Monday' },
    { id: 3, short: 'T', abbr: 'Tue', name: 'Tuesday' },
    { id: 4, short: 'W', abbr: 'Wed', name: 'Wednesday' },
    { id: 5, short: 'T', abbr: 'Thu', name: 'Thursday' },
    { id: 6, short: 'F', abbr: 'Fri', name: 'Friday' },
    { id: 7, short: 'S', abbr: 'Sat', name: 'Saturday' },
] as const;

/**
 * Unambiguous day labels for plain text ("M W F", "Sa Su"), where a lone S or T
 * could mean either of two days.
 */
export const DAY_TEXT_LABELS: Readonly<Record<number, string>> = {
    1: 'Su',
    2: 'M',
    3: 'Tu',
    4: 'W',
    5: 'Th',
    6: 'F',
    7: 'Sa',
};

/** tabasco-react schedule.jsx OVERLAP_PALETTE: base (one-off / recurring) layers. */
export const OVERLAP_PALETTE = [
    '#521B93',
    '#9437FF',
    '#0432FF',
    '#942093',
    '#941651',
    '#FF2F92',
    '#009193',
    '#011893',
] as const;

/** tabasco-react schedule.jsx OVERLAP_PALETTE_INSERTS: insert layers. */
export const OVERLAP_PALETTE_INSERTS = [
    '#76D6FF',
    '#00FA00',
    '#EBEBEB',
    '#73FDD6',
    '#FF85FF',
    '#D5FC79',
] as const;

export const SILENCE_COLOR = '#D72501';
export const MESSAGE_COLOR = '#FFD579';
